import { type HiddenStory } from '../repositories/readerFeedbackRepository';
import { type UserEventContext } from '../repositories/storyRepository';

/**
 * Where a reader hide applies. A hide always applies to the view it was made
 * in; its reason can widen that. "Not about X" adds feed X (or the search
 * query), and every other reason hides the story everywhere. A hide with no
 * reason stays in the view it was made in.
 *
 * The rows are the durable record in reader_story_feedback, and a story can
 * have several active rows (hidden in X, later hidden in Y for another reason).
 * This module is the one place that decides which of them hide a story in a
 * given view, and so which of them Unhide retracts.
 */
export type HideView = { kind: 'home' } | { kind: 'feed'; feedId: string } | { kind: 'query'; query: string };
export type HideSurface = 'home' | 'results' | 'feed_page';

type Scope = { everywhere: boolean; home: boolean; feeds: string[]; queries: string[] };

export function hideViewOf(context: UserEventContext): HideView {
  if (context.surface === 'feed' && context.surfaceId) return { kind: 'feed', feedId: context.surfaceId };
  const query = String(context.properties?.query || '').trim();
  if (context.surface === 'search' && query) return { kind: 'query', query };
  return { kind: 'home' };
}

export function hideSurfaceOf(view: HideView): HideSurface {
  return view.kind === 'feed' ? 'feed_page' : view.kind === 'query' ? 'results' : 'home';
}

/**
 * Properties a new hide must carry to record its view. The origin uses its own
 * keys because the reason PATCH merges `feed_id`/`query` into the same object.
 */
export function hideOriginProperties(view: HideView): Record<string, string> {
  if (view.kind === 'feed') return { origin_feed_id: view.feedId };
  if (view.kind === 'query') return { origin_query: view.query };
  return {};
}

export function hideScope(row: HiddenStory): Scope {
  const p = row.properties || {};
  const scope: Scope = { everywhere: false, home: false, feeds: [], queries: [] };
  if (row.reason && row.reason !== 'not_about_feed') return { ...scope, everywhere: true };
  const origin = row.surface === 'home' ? 'home' : row.surface === 'feed_page' ? p.origin_feed_id : p.origin_query;
  if (typeof origin === 'string' && origin) {
    if (row.surface === 'home') scope.home = true;
    else if (row.surface === 'feed_page') scope.feeds.push(origin);
    else scope.queries.push(origin);
  } else if (!row.reason) {
    // Rows written before hides were scoped carry no origin view. A reason-less one
    // hid everywhere then and still does; a "not about" one is scoped by its reason.
    return { ...scope, everywhere: true };
  }
  if (row.reason === 'not_about_feed') {
    if (typeof p.feed_id === 'string' && p.feed_id) scope.feeds.push(p.feed_id);
    if (typeof p.query === 'string' && p.query.trim()) scope.queries.push(p.query.trim());
  }
  return scope;
}

/**
 * The rows that hide one story in `view`, in `rows` order (newest first, as the
 * API lists them); empty means visible.
 * Home merges several feeds, so feed-scoped hides remove a story from Home only
 * once they cover every feed that brought it there.
 */
export function rowsHidingStory(rows: HiddenStory[], view: HideView, matchedFeedIds: string[]): HiddenStory[] {
  const scoped = rows.map(row => ({ row, scope: hideScope(row) }));
  const hiding = scoped.filter(({ scope }) => scope.everywhere
    || (view.kind === 'home' && scope.home)
    || (view.kind === 'feed' && scope.feeds.includes(view.feedId))
    || (view.kind === 'query' && scope.queries.includes(view.query)));
  if (view.kind === 'home' && matchedFeedIds.length > 0) {
    const covering = scoped.filter(({ scope }) => scope.feeds.some(id => matchedFeedIds.includes(id)));
    const covered = new Set(covering.flatMap(({ scope }) => scope.feeds));
    if (matchedFeedIds.every(id => covered.has(id))) hiding.push(...covering.filter(item => !hiding.includes(item)));
  }
  return scoped.filter(item => hiding.includes(item)).map(({ row }) => row);
}
